<script setup>
import { ref } from "vue";
import { GqlService } from "@js/services/gql";
import ExpeditionTile from "@js/vue/components/ExpeditionTile.vue";

// Props for receiving expeditions data from Twig
const props = defineProps({
	expeditions: {
		type: Array,
		default: () => [],
	},
	shipmentTypes: {
		type: Array,
		default: () => [],
	},
	isForwarderView: {
		type: Boolean,
		default: false,
	},
	sanctionWarningText: {
		type: String,
		default: "",
	},
});

// Check if expedition has sanction law violation
const hasSanctionViolation = (expedition) => {
	return (
		!expedition.sanctionApproved &&
		(expedition.hasSanctionTicket || expedition.isInSanctionList)
	);
};

// State for delete confirmation modal
const showDeleteModal = ref(false);
const expeditionToDelete = ref(null);
const isDeleting = ref(false);
const deleteError = ref("");
const expeditions = ref(props.expeditions);

const today = new Date().toISOString().split("T")[0];

const isTransportDateInFuture = (expedition) => {
	return getTransportDateForComparison(expedition) >= today;
};

const hasPolicyStatus = (expedition, statuses) => {
	if (!expedition.policy?.exPolicyStatus?.value) return false;
	return statuses.includes(expedition.policy.exPolicyStatus.value);
};

const getShipmentType = (expedition) => {
	return props.shipmentTypes.find(
		(type) =>
			type.label.toLowerCase() === expedition.exShipmentType.toLowerCase(),
	);
};

const getTransportDate = (expedition) => {
	if (!expedition.transportDate) return "";

	//TODO: Check format coming from twig to be consistent
	const date = expedition.transportDate.date || expedition.transportDate;
	return new Date(date).toLocaleDateString("en-GB");
};

const getTransportDateForComparison = (expedition) => {
	if (!expedition.transportDate) return "";
	const date = expedition.transportDate.date || expedition.transportDate;
	return new Date(date).toISOString().split("T")[0];
};

const handleEditInsurance = (expedition, event) => {
	event.stopPropagation();

	if (!expedition.policy || !expedition.policy.uid) return;
	window.location.href = `/expeditions/policies/${expedition.policy.uid}/specify`;
};

const handleDeleteExpedition = (expedition, event) => {
	event.stopPropagation();
	expeditionToDelete.value = expedition;
	showDeleteModal.value = true;
	deleteError.value = "";
};

const confirmDeleteExpedition = async () => {
	if (!expeditionToDelete.value) return;

	isDeleting.value = true;
	deleteError.value = "";
	let json;
	try {
		json = await GqlService.request(
			`
			mutation DeleteExpedition($expeditionId: ID!) {
				deleteExpedition(expeditionId: $expeditionId)
			}
		`,
			{
				expeditionId: expeditionToDelete.value.id,
			},
		);
	} catch (error) {
		deleteError.value =
			error.message || "An error occurred while deleting the expedition";
		isDeleting.value = false;
		return;
	}

	if (json.errors) {
		deleteError.value = json.errors[0].message;
		isDeleting.value = false;
		return;
	}

	if (json.data.deleteExpedition) {
		expeditions.value = expeditions.value.filter(
			(expedition) => expedition.id !== expeditionToDelete.value.id,
		);
	} else {
		deleteError.value = "Failed to delete expedition";
	}
	isDeleting.value = false;
	showDeleteModal.value = false;
};

const cancelDelete = () => {
	showDeleteModal.value = false;
	expeditionToDelete.value = null;
	deleteError.value = "";
};

const handleUpdateExpedition = (expedition, event) => {
	event.stopPropagation();
	window.location.href = `/expeditions/expeditions/${expedition.uid}/edit`;
};

const handleViewPolicy = (expedition, event) => {
	event.stopPropagation();

	if (expedition.policy && expedition.policy.uid) {
		window.location.href = `/expeditions/policies/${expedition.policy.uid}`;
	}
};

const handleSpecifyPolicy = async (expedition, event) => {
	event.stopPropagation();

	const json = await GqlService.request(
		`
		mutation CreatePolicy($expeditionId: ID!) {
			createPolicy(expeditionId: $expeditionId) {
				uid
			}
		}
	`,
		{
			expeditionId: expedition.id,
		},
	);

	if (json.data.createPolicy) {
		window.location.href = `/expeditions/policies/${json.data.createPolicy.uid}/specify`;
	}
};

//Commented until storage extensions are back. Linters cry about an unused function while button is commented.

// const handleSpecifyStorage = (expedition, event) => {
// 	event.stopPropagation();
// 	window.location.href = `/expeditions/policies/${expedition.policy.uid}/add-storage`;
// };

const getShipmentStatusClass = (expedition) => {
	if (!expedition.policy?.exPolicyStatus?.value) {
		return ""; // No policy - default blue
	}

	//Mapped to bolstA classes to use their styling
	if (hasPolicyStatus(expedition, ["incomplete", "lost"])) {
		return "incomplete"; // Red
	}
	if (hasPolicyStatus(expedition, ["complete", "confirmed"])) {
		return "complete"; // Green
	}
	return ""; // Default blue
};

//Commented until storage extensions are back. Linters cry about an unused function while button is commented.

// const canAddStorageInsurance = (expedition) => {
// 	if (!expedition.policy) return false;
// 	if (!hasPolicyStatus(expedition, ["complete", "confirmed"])) return false;
// 	if (!isTransportDateInFuture(expedition)) return false;
// 	return expedition.policy.children.length === 0;
// };
</script>

<template>
	<div>
		<!-- Expeditions List -->
		<div v-if="expeditions.length" class="shipments">
			<expedition-tile
				v-for="expedition in expeditions"
				:key="expedition.id"
				:shipmentType="getShipmentType(expedition)"
				:shipName="expedition.shipName"
				:originAddress="expedition.origin?.address"
				:destinationAddress="expedition.destination?.address"
				:reference="expedition.reference ?? '-'"
				:transportDate="getTransportDate(expedition)"
				:statusClass="
					isForwarderView ? null : getShipmentStatusClass(expedition)
				"
				:title="expedition.policy?.title"
				:storagePolicies="isForwarderView ? null : expedition.policy?.children"
			>
				<template v-slot:header-buttons>
					<!-- Action buttons for past transport dates -->
					<div
						v-if="
							!expedition.policy &&
							isTransportDateInFuture(expedition) &&
							isForwarderView
						"
						class="col-auto"
					>
						<div class="d-flex">
							<button
								class="btn btn-edit"
								@click="handleUpdateExpedition(expedition, $event)"
								title="Edit expedition"
							>
								<i class="ri-pencil-fill"></i>
							</button>
							<button
								class="btn btn-delete"
								@click="handleDeleteExpedition(expedition, $event)"
								title="Delete expedition"
							>
								<i class="ri-delete-bin-line"></i>
							</button>
						</div>
					</div>

					<!-- Policy button for forwarders -->
					<div
						v-if="
							expedition.policy &&
							hasPolicyStatus(expedition, ['complete', 'confirmed']) &&
							isForwarderView
						"
						class="col-auto"
					>
						<div class="d-flex">
							<button
								class="btn btn-policy"
								@click="handleViewPolicy(expedition, $event)"
								title="View Policy"
							>
								<span class="me-2">View policy</span>
								<i class="ri-arrow-right-line"></i>
							</button>
						</div>
					</div>
					<div v-if="!isForwarderView" class="col-auto">
						<div class="d-flex" style="padding: 10px">
							<span
								><small>{{
									expedition.policy?.exPolicyStatus?.label ?? ""
								}}</small></span
							>
						</div>
					</div>
				</template>

				<!-- Sanction Law Warning -->
				<template v-slot:warning v-if="hasSanctionViolation(expedition)">
					<div v-if="sanctionWarningText" v-html="sanctionWarningText"></div>
					<div v-else>
						Either origin or destination country is on the sanction law
						countries list.
					</div>
				</template>

				<template v-slot:footer-button-left v-if="!isForwarderView">
					<!-- Storage button -->
					<div class="specify-storage">
						<!-- Storage extensions are built but not used yet, so we comment the button in order to prevent people from starting the flow-->
						<!-- When uncommenting, also uncomment canAddStorageInsurance() and handleSpecifyStorage() at top of file.-->

						<!-- <button
							v-if="canAddStorageInsurance(expedition)"
							type="button"
							class="btn btn-rounded btn-warning btn-actions"
							@click="handleSpecifyStorage(expedition, $event)"
						>
							<span>Add storage insurance</span>
							<i class="ri-arrow-right-line"></i>
						</button> -->
					</div>
				</template>

				<template v-slot:footer-button-right v-if="!isForwarderView">
					<div class="specify-insurance">
						<button
							v-if="!expedition.policy"
							type="button"
							class="btn btn-rounded btn-blue btn-actions"
							@click="handleSpecifyPolicy(expedition, $event)"
						>
							<span>Specify Insurance</span>
							<i class="ri-arrow-right-line"></i>
						</button>
						<button
							v-if="hasPolicyStatus(expedition, ['incomplete', null])"
							type="button"
							class="btn btn-rounded btn-blue btn-actions"
							@click="handleEditInsurance(expedition, $event)"
						>
							<span>Edit</span>
							<i class="ri-arrow-right-line"></i>
						</button>
						<button
							v-if="hasPolicyStatus(expedition, ['complete', 'confirmed'])"
							type="button"
							class="btn btn-rounded btn-blue btn-actions"
							@click="handleViewPolicy(expedition, $event)"
						>
							<span>View</span>
							<i class="ri-arrow-right-line"></i>
						</button>
					</div>
				</template>

				<!--				<template #extend-storage="{ storagePolicy }">-->
				<!-- TODO: handle the extend storage click and condition -->
				<!--					<button type="button" class="btn btn-rounded btn-blue btn-actions">-->
				<!--						<span>TODO: Extend storage {{ storagePolicy.id }}</span>-->
				<!--						<i class="ri-arrow-right-line"></i>-->
				<!--					</button>-->
				<!--				</template>-->
			</expedition-tile>
		</div>

		<!-- Empty State -->
		<div v-else class="shipments empty mb-4 text-center">
			<p class="text-muted">
				<em>There are no expeditions yet. Please add an expedition.</em>
			</p>
		</div>

		<!-- Delete Confirmation Modal -->
		<div
			v-if="showDeleteModal"
			class="modal fade show d-block"
			style="background-color: rgba(0, 0, 0, 0.5)"
			tabindex="-1"
		>
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header border-0 pb-0">
						<button
							type="button"
							class="btn-close"
							@click="cancelDelete"
							:disabled="isDeleting"
						></button>
					</div>
					<div class="modal-body py-4 text-center">
						<h4 class="modal-title mb-3">
							Are you sure you want to delete this expedition?
						</h4>
						<p class="text-muted mb-4">
							After confirming the expedition will be removed permanently and
							cannot be restored.
						</p>

						<!-- Error message -->
						<div v-if="deleteError" class="alert alert-danger" role="alert">
							{{ deleteError }}
						</div>

						<div class="d-flex justify-content-center gap-3">
							<button
								type="button"
								class="btn btn-rounded btn-outline-blue"
								@click="cancelDelete"
								:disabled="isDeleting"
							>
								<i class="ri-arrow-left-line"></i>
								<span>No, go back</span>
							</button>
							<button
								type="button"
								class="btn btn-rounded btn-blue"
								@click="confirmDeleteExpedition"
								:disabled="isDeleting"
							>
								<span v-if="isDeleting">Deleting...</span>
								<span v-else>Yes, delete expedition</span>
								<i v-if="!isDeleting" class="ri-check-line"></i>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.btn-edit {
	background-color: rgba(255, 255, 255, 0) !important;
}

.btn-delete {
	background-color: rgba(255, 255, 255, 0) !important;
}

.btn-policy {
	background-color: rgba(255, 255, 255, 0) !important;
	color: white !important;
	border-radius: 6px !important;
	padding: 8px 16px !important;
	border: none !important;
	font-size: 14px !important;
}

.btn-policy:hover {
	background-color: #0056b3 !important;
	color: white !important;
}

.specify-insurance {
	padding: 10px;
}

.specify-storage {
	padding: 10px;
}

.btn-warning {
	color: white !important;
}

.btn-actions {
	margin: 15px;
}
</style>
